// @flow
import { useCallback, useRef, useMemo } from 'react';

type Handler<T> = (...args: $ReadOnlyArray<T>) => void;
type RegisterFn<T> = (handler: Handler<T>) => void;

type Registration<T> = {
  register: RegisterFn<T>,
  unregister: RegisterFn<T>,
};

export function useHandleWrapper<T>(
  originalHandler: Handler<T>,
): [Handler<T>, Registration<T>] {

  const handlersRef = useRef<Set<Handler<T>>>(new Set());
  const originalHandlerRef = useRef<Handler<T>>(originalHandler);

  originalHandlerRef.current = originalHandler;

  const wrappedHandler = useCallback(
    (...args: $ReadOnlyArray<T>) => {
      handlersRef.current.forEach(handler => handler(...args));
      if (originalHandlerRef.current) {
        originalHandlerRef.current(...args);
      }

    },
    [
      handlersRef,
      originalHandlerRef,
    ],
  );

  const registration = useMemo(
    () => ({
      register: (handler: Handler<T>) => {
        handlersRef.current.add(handler);
      },
      unregister: (handler: Handler<T>) => {
        handlersRef.current.delete(handler);
      },
    }),
    [
      handlersRef,
    ],
  );

  return [wrappedHandler, registration];
}

export default useHandleWrapper;
